import type { FilterSchema } from '@/shared/components/filter/validation'
import type { FilterRuleEntity } from '@/shared/components/query-builder'

import { FilterIcon, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useStoredFiltersKeys } from '@/entities/user'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import FiltersPopover from '@/shared/components/filter/FiltersPopover'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import SearchField from '@/shared/components/SearchField'
import { Button } from '@/shared/components/ui/button'
import { Header } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'

import { getFilterFields } from '../../lib/helpers'
import { useAnchorStrategiesDataChoices } from '../../lib/useAnchorStrategiesDataChoices'
import CreateStrategyTrigger from './CreateStrategyTrigger'

export default function StrategiesTableToolbar() {
	const { t } = useTranslation()

	const [storedQuickFiltersKeys, saveStoredQuickFiltersKeys] =
		useStoredFiltersKeys('anchor_strategy_quick_filters_keys')
	const [{ filters, search }, setQuery] = useLocationQuery()
	const dataChoices = useAnchorStrategiesDataChoices()
	const filterFields = getFilterFields(t)
	const handleApplyFilter = (filters: FilterRuleEntity[]) => {
		ampli.tableAnchorStrategyApplyFilter()
		setQuery({ filters: filters?.length ? filters : null })
	}

	return (
		<Header className='shrink-0 items-center justify-between py-2'>
			<div className='flex items-center space-x-2'>
				<SearchField
					onValueChange={(search) => setQuery({ search })}
					value={search}
					placeholder={t('general_search')}
				/>
				<FiltersPopover
					fields={filterFields}
					onApply={handleApplyFilter}
					dataChoices={dataChoices}
					filters={(filters as FilterSchema[]) || []}
				>
					<Button>
						<FilterIcon />
						{t('general_filter')}&nbsp;
						{filters.length ? `(${filters.length})` : ''}
					</Button>
				</FiltersPopover>
				<Separator orientation='vertical' className='h-5!' />
				<QuickFiltersBar
					storedKeys={storedQuickFiltersKeys}
					onSaveStoredKeys={saveStoredQuickFiltersKeys}
					rules={filters ?? []}
					fieldsConfig={getFiledConfig(filterFields, dataChoices)}
					onApply={handleApplyFilter}
					limit={3}
				/>
			</div>
			<div className='flex items-center space-x-1'>
				<Tooltip>
					<TooltipTrigger asChild>
						<CreateStrategyTrigger>
							<Button
								data-testid='create-strategy-button'
								variant='primary'
								size='icon'
							>
								<PlusIcon />
							</Button>
						</CreateStrategyTrigger>
					</TooltipTrigger>
					<TooltipContent>
						{t('anchoring_management.create_strategy.title')}
					</TooltipContent>
				</Tooltip>
			</div>
		</Header>
	)
}
